<template>
  <div class="app">
    <template v-if="this.state != null">
      <v-tabs show-arrows v-model="tabs" background-color="transparent">
        <v-tabs-slider color="primary"></v-tabs-slider>

        <v-tab>{{ $t("advanced.headers.basic_diag") }}</v-tab>
        <v-tab>{{ $t("advanced.headers.power_n_energy") }}</v-tab>
        <v-tab>{{ $t("advanced.headers.inverter") }}</v-tab>
        <v-tab>{{ $t("advanced.headers.grid") }}</v-tab>
        <v-tab>{{ $t("advanced.headers.output") }}</v-tab>
        <v-tab>{{ $t("advanced.headers.mppt") }}</v-tab>
        <v-tab>{{ $t("advanced.headers.battery") }}</v-tab>
        <v-tab>{{ $t("advanced.headers.dc") }}</v-tab>
        <v-tab>{{ $t("advanced.headers.temperatures") }}</v-tab>
        <v-tab>{{ $t("advanced.headers.fastChart") }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tabs">
        <!-- Základní diagnostika -->
        <v-tab-item>
          <v-row class="my-2 mx-1">
            <v-col cols="12">
              <h2>Basic</h2>
            </v-col>
            <v-col cols="12" md="6">
              <AdvancedLogic :unitName="'ccu'" :unitState="state" :showHeader="true" :filter="['Basic']" :width="widthLogic" />
            </v-col>
            <v-col cols="12" md="6">
              <AdvancedLogic :unitName="'inverter'" :unitState="state.inverter" :showHeader="true" :filter="['Basic', 'BasicSafety']" :width="widthLogic" />
            </v-col>

            <v-col cols="12" md="6" v-for="(item, index) in state.mppt" :key="index + 'BasicA'">
              <AdvancedCoreHeader :name="$t('advanced.headers.pv') + ' ' + (index + 1)" heading="h4" :width="widthLogic" />
              <AdvancedLogic :unitName="'pv'" :unitState="item" :filter="['Basic', 'BasicSafety']" :width="widthLogic" />
            </v-col>

            <v-col cols="12" md="6" v-for="(item, index) in state.battery.batteries" :key="index + 'BasicB'">
              <AdvancedCoreHeader :name="$t('advanced.headers.dab') + ' ' + (index + 1)" heading="h4" :width="widthLogic" />
              <AdvancedLogic :unitName="'dab'" :unitState="item" :filter="['Basic', 'BasicSafety']" :width="widthLogic" />
            </v-col>
          </v-row>

          <v-row class="my-2 mx-1">
            <v-col cols="12">
              <h2>State & Errors</h2>
            </v-col>
            <v-col cols="12" md="6">
              <AdvancedLogic :unitName="'inverter'" :unitState="state.inverter" :showHeader="true" :filter="['State', 'StateSafety', 'Errors', 'ErrorsSafety']" :width="widthLogic" />
            </v-col>

            <v-col cols="12" md="6" v-for="(item, index) in state.mppt" :key="index + 'StateA'">
              <AdvancedCoreHeader :name="$t('advanced.headers.pv') + ' ' + (index + 1)" heading="h4" :width="widthLogic" />
              <AdvancedLogic :unitName="'pv'" :unitState="item" :filter="['State', 'StateSafety', 'Errors', 'ErrorsSafety']" :width="widthLogic" />
            </v-col>

            <v-col cols="12" md="6" v-for="(item, index) in state.battery.batteries" :key="index + 'StateB'">
              <AdvancedCoreHeader :name="$t('advanced.headers.dab') + ' ' + (index + 1)" heading="h4" :width="widthLogic" />
              <AdvancedLogic :unitName="'dab'" :unitState="item" :filter="['State', 'StateSafety', 'Errors', 'ErrorsSafety']" :width="widthLogic" />
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- Výkony -->
        <v-tab-item>
          <v-row class="my-2 mx-1">
            <v-col cols="12" md="6">
              <h3 class="mx-4">{{ $t("advanced.variables.power") }}</h3>
              <AdvancedInOutPower :unitName="'load'" :unitState="state.load" :filter="['P']" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'grid'" :unitState="state.grid" :filter="['P']" :width="widthInOut" />
              <AdvancedCoreHeader :name="$t('advanced.headers.inverter')" heading="h4" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'inverter'" :unitState="state.inverter" :filter="['P']" :width="widthInOut" />
              <AdvancedCoreHeader :name="$t('advanced.headers.dab')" heading="h4" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'dab'" :unitState="state.battery" :filter="['P']" :width="widthInOut" />
              <AdvancedCoreHeader :name="$t('advanced.headers.pv')" heading="h4" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'pv'" :unitState="state.pv" :filter="['P']" :width="widthInOut" />
            </v-col>
            <v-col cols="12" md="6"></v-col>

            <v-col cols="12" md="6" v-for="(item, index) in state.mppt" :key="index + 'PowerA'">
              <AdvancedCoreHeader :name="$t('advanced.headers.pv') + ' ' + (index + 1)" heading="h4" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'pv'" :unitState="item" :filter="['P']" :width="widthInOut" />
            </v-col>

            <v-col cols="12" md="6" v-for="(item, index) in state.battery.batteries" :key="index + 'PowerB'">
              <AdvancedCoreHeader :name="$t('advanced.headers.dab') + ' ' + (index + 1)" heading="h4" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'dab'" :unitState="item" :filter="['P']" :width="widthInOut" />
            </v-col>

            <v-col cols="12" md="6">
              <br />
              <h3 class="mx-4">{{ $t("advanced.variables.energy") }}</h3>
              <AdvancedInOutPower :unitName="'load'" :unitState="state.load" :filter="['E']" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'grid'" :unitState="state.grid" :filter="['E']" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'inverter'" :unitState="state.inverter" :filter="['E']" :width="widthInOut" />

              <AdvancedCoreHeader :name="$t('advanced.headers.dab')" heading="h4" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'dab'" :unitState="state.battery" :filter="['E']" :width="widthInOut" />

              <AdvancedCoreHeader :name="$t('advanced.headers.pv')" heading="h4" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'pv'" :unitState="state.pv" :filter="['E']" :width="widthInOut" />
            </v-col>
            <v-col cols="12" md="6"></v-col>
            <v-col cols="12" md="6" v-for="(item, index) in state.mppt" :key="index + 'EnergyA'">
              <AdvancedCoreHeader :name="$t('advanced.headers.pv') + ' ' + (index + 1)" heading="h4" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'pv'" :unitState="item" :filter="['E']" :width="widthInOut" />
            </v-col>

            <v-col cols="12" md="6" v-for="(item, index) in state.battery.batteries" :key="index + 'EnergyB'">
              <AdvancedCoreHeader :name="$t('advanced.headers.dab') + ' ' + (index + 1)" heading="h4" :width="widthInOut" />
              <AdvancedInOutPower :unitName="'dab'" :unitState="item" :filter="['E']" :width="widthInOut" />
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- Střídač -->
        <v-tab-item>
          <v-row class="my-2 mx-1">
            <v-col cols="12" md="6">
              <h2>Basic</h2>
              <AdvancedLogic :unitName="'inverter'" :unitState="state.inverter" :showHeader="false" :filter="['Basic', 'BasicSafety']" :width="widthLogic" />
              <h2>State & Errors</h2>
              <AdvancedLogic :unitName="'inverter'" :unitState="state.inverter" :showHeader="false" :filter="['State', 'StateSafety', 'Errors', 'ErrorsSafety']" :width="widthLogic" />
            </v-col>

            <v-col cols="12" md="6">
              <AdvancedInOutPower :unitName="'inverter'" :unitState="state.inverter" :width="widthInOut" />
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- Grid -->
        <v-tab-item>
          <v-row class="my-2 mx-1">
            <v-col cols="12" md="6">
              <AdvancedInOutPower :unitName="'grid'" :unitState="state.grid" :width="widthInOut" />
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- Výstup -->
        <v-tab-item>
          <v-row class="my-2 mx-1">
            <v-col cols="12" md="6">
              <AdvancedInOutPower :unitName="'load'" :unitState="state.load" :width="widthInOut" />
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- FVE -->
        <v-tab-item>
          <v-row class="my-2 mx-1">
            <v-col cols="12" md="6" v-for="(item, index) in state.mppt" :key="index + 'D'">
              <AdvancedCoreHeader :name="$t('advanced.headers.pv') + ' ' + (index + 1)" heading="h4" :width="widthLogic" />
              <AdvancedLogic :unitName="'pv'" :unitState="item" :filter="['State', 'StateSafety', 'Errors', 'ErrorsSafety']" :width="widthLogic" />
              <AdvancedInOutPower :unitName="'pv'" :unitState="item" :width="widthLogic" />
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- Battery -->
        <v-tab-item>
          <v-row class="my-2 mx-1">
            <v-col cols="12" md="6" v-for="(item, index) in state.battery.batteries" :key="index + 'D'">
              <AdvancedCoreHeader :name="$t('advanced.headers.dab') + ' ' + (index + 1)" heading="h4" :width="widthLogic" />
              <AdvancedLogic :unitName="'dab'" :unitState="item" :filter="['State', 'StateSafety', 'Errors', 'ErrorsSafety']" :width="widthLogic" />
              <AdvancedInOutPower :unitName="'dab'" :unitState="item" :width="widthLogic" />
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- DC Bus -->
        <v-tab-item>
          <v-row class="my-2 mx-1">
            <v-col cols="12" md="6" sm="8">
              <AdvancedCoreHeader :name="$t('advanced.headers.inverter')" heading="h4" :width="widthInOut" />

              <AdvancedDC :unitName="'inverter'" :unitState="state.inverter" :filter="['U']" :width="widthInOut" />

              <div v-for="(item, index) in state.battery.batteries" :key="index + 'XXX1'">
                <AdvancedCoreHeader :name="$t('advanced.headers.dab') + ' ' + (index + 1)" heading="h4" :width="widthInOut" />
                <AdvancedDC :unitName="'dab'" :unitState="item" :filter="['U']" :width="widthInOut" />
              </div>

              <div v-for="(item, index) in state.mppt" :key="index + 'XXX2'">
                <AdvancedCoreHeader :name="$t('advanced.headers.pv') + ' ' + (index + 1)" heading="h4" :width="widthInOut" />
                <AdvancedDC :unitName="'pv'" :unitState="item" :filter="['U']" :width="widthInOut" />
              </div>
            </v-col>

            <v-col cols="12" md="6" sm="8">
              <AdvancedCoreHeader :name="$t('advanced.headers.inverter')" heading="h4" :width="widthInOut" />

              <AdvancedDC :unitName="'inverter'" :unitState="state.inverter" :filter="['I']" :width="widthInOut" />

              <div v-for="(item, index) in state.battery.batteries" :key="index + 'XXX3'">
                <AdvancedCoreHeader :name="$t('advanced.headers.dab') + ' ' + (index + 1)" heading="h4" :width="widthInOut" />
                <AdvancedDC :unitName="'dab'" :unitState="item" :filter="['I']" :width="widthInOut" />
              </div>

              <div v-for="(item, index) in state.mppt" :key="index + 'XXX4'">
                <AdvancedCoreHeader :name="$t('advanced.headers.pv') + ' ' + (index + 1)" heading="h4" :width="widthInOut" />
                <AdvancedDC :unitName="'pv'" :unitState="item" :filter="['I']" :width="widthInOut" />
              </div>
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- Temperature -->
        <v-tab-item>
          <v-row class="my-2 mx-1">
            <v-col cols="12" md="6" sm="8">
              <AdvancedTempsAndFans :showHeader="true" :unitName="'inverter'" :unitState="state.inverter" :filter="['Temps']" :width="widthTemps" />
            </v-col>

            <v-col cols="12" md="6" sm="8"> </v-col>

            <v-col cols="12" md="6" sm="8" v-for="(item, index) in state.mppt" :key="index + 'TempsA'">
              <AdvancedTempsAndFans :showHeader="true" :unitName="'mppt'" :unitIndex="index + 1" :unitState="item" :filter="['Temps']" :width="widthTemps" />
            </v-col>

            <v-col cols="12" md="6" sm="8" v-for="(item, index) in state.battery.batteries" :key="index + 'TempsB'">
              <AdvancedTempsAndFans :showHeader="true" :unitName="'dab'" :unitIndex="index + 1" :unitState="item" :filter="['Temps']" :width="widthTemps" />
            </v-col>

            <v-col cols="12" md="6" sm="8">
              <AdvancedTempsAndFans :showHeader="true" :unitName="'inverter'" :unitState="state.inverter" :filter="['Fans']" :width="widthFans" />
            </v-col>

            <v-col cols="12" md="6" sm="8"> </v-col>

            <v-col cols="12" md="6" sm="8" v-for="(item, index) in state.mppt" :key="index + 'FansA'">
              <AdvancedTempsAndFans :showHeader="true" :unitName="'mppt'" :unitIndex="index + 1" :unitState="item" :filter="['Fans']" :width="widthFans" />
            </v-col>

            <v-col cols="12" md="6" sm="8" v-for="(item, index) in state.battery.batteries" :key="index + 'FansB'">
              <AdvancedTempsAndFans :showHeader="true" :unitName="'dab'" :unitIndex="index + 1" :unitState="item" :filter="['Fans']" :width="widthFans" />
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item>
          <AnyChart :heightSheet="600" :heightChart="550" :dark="$vuetify.theme.dark" :tabs="6" :header="$t('home.dashboardChart.consumptionChart4.name')" :start="''" :stop="''" :ct="'Line'" :card="true" />
        </v-tab-item>
      </v-tabs-items>
    </template>
    <template v-else>
      <LoadingData />
    </template>
    <UpgradeCore />
  </div>
</template>

<script>
import UpgradeCore from "../../components/upgrade/UpgradeCore.vue";
import AnyChart from "../../components/charts/AnyChart.vue";
import LoadingData from "../../components/LoadingData.vue";

import AdvancedCoreHeader from "../../components/table/AdvancedCoreHeader.vue";

import AdvancedLogic from "../../components/table/AdvancedLogic.vue";
import AdvancedDC from "../../components/table/AdvancedDC.vue";
import AdvancedInOutPower from "../../components/table/AdvancedInOutPower.vue";
import AdvancedTempsAndFans from "../../components/table/AdvancedTempsAndFans.vue";

import { ServiceClient } from "../../service_client/index.js";

export default {
  components: {
    AdvancedCoreHeader,
    AdvancedLogic,
    AdvancedDC,
    AdvancedInOutPower,
    AdvancedTempsAndFans,
    AnyChart,
    LoadingData,
    UpgradeCore,
  },
  data() {
    return {
      timer: null,

      widthLogic: 42,
      widthInOut: 70,

      widthTemps: 50,
      widthFans: 50,

      tabs: null,

      dense: true,
      fixedHeader: true,

      state: null,
    };
  },
  methods: {
    convertDecToHex(number) {
      return number.toString(16);
    },
    pollData() {
      this.timer = setInterval(() => {
        this.getData();
      }, 1000);
    },
    getData() {
      ServiceClient.dataCore(ServiceClient.getAPIUrl("main"), null, null, "GET")
        .then((data) => {
          this.state = data;
        })
        .catch((error) => {
          this.state = ServiceClient.handleUnauthorized(error, this.$router);
        });
    },
  },
  mounted() {
    this.getData();
  },
  created() {
    this.pollData();
  },
  beforeDestroy: function () {
    clearInterval(this.timer);
  },
};
</script>

<style scoped>
.v-card {
  min-height: 500px;
}
.v-simple-table {
  min-height: 450px;
}
td {
  width: 50%;
}
</style>
